import { ActivityIndicator } from 'react-native-paper';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useUnistyles } from 'react-native-unistyles';
import { supabase } from '@/data/client';
import { registerDevice } from '@/utils/deviceRegistration';
import { getUsersOrganizations } from '@/data/organization';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import usePlatform from '@/utils/hooks/usePlatform';
import { openURL } from '@/utils/linking';
import AuthPage, { AuthHeader } from '@/components/General/AuthPage';

// URL the Stripe setup session sends the customer back to after checkout.
// app.reachchms.com is app-claimed, so this re-enters the app at login — the
// session is already established, so checkAuth advances them onward.
const BILLING_RETURN_URL = 'https://app.reachchms.com/auth/login';

/**
 * Completes a website org signup inside the app.
 *
 * reachchms.com/signup is claimed by iOS/Android universal links, so a user
 * who clicks their verification email on a phone with the app installed lands
 * here instead of the website's completion page. The auth payload arrives as
 * the '#' fragment param (Supabase implicit style) or top-level query params;
 * Supabase error redirects land here too (#error=...&error_description=...).
 *
 * On a verified session we finish what the website would have done: create the
 * organization from the signup metadata, then hand paid signups off to Stripe
 * in the external browser (checkout domain isn't app-claimed).
 */
const SignupScreen = () => {
    const [noPayload, setNoPayload] = useState(false);
    const startedRef = useRef(false);

    const queryClient = useQueryClient();
    const { theme: { colors } } = useUnistyles();
    const { isWeb, isAndroid } = usePlatform();
    const router = useRouter();
    const localParams = useLocalSearchParams();

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        const first = (v) => Array.isArray(v) ? v[0] : v;
        const hashParams = new URLSearchParams(first(localParams['#']) || '');
        const param = (key) => first(localParams[key]) || hashParams.get(key);

        const bailToLogin = (message) => {
            Toast.show({ type: 'error', text2: message });
            router.replace('/auth/login');
        };

        const establishSession = async () => {
            const authError = param('error');
            if (authError) {
                bailToLogin(param('error_description') || 'Your signup link is no longer valid. Please sign up again.');
                return null;
            }

            const accessToken = param('access_token');
            const refreshToken = param('refresh_token');
            const tokenHash = param('token_hash');
            const code = param('code');

            if (accessToken && refreshToken) {
                const { data, error } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken,
                });
                if (error) throw error;
                return data?.session || null;
            }

            if (tokenHash) {
                // Website signup verification uses type 'signup'; 'email'
                // covers generic email-confirmation links in the same form.
                let { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'signup' });
                if (error) {
                    ({ data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'email' }));
                }
                if (error) throw error;
                return data?.session || null;
            }

            if (code) {
                // PKCE codes need the verifier from the browser that started
                // signup — cross-device it doesn't exist, so failure here is
                // expected and falls through to login (where an existing
                // session, if any, still advances the user).
                const { data, error } = await supabase.auth.exchangeCodeForSession(code);
                if (error) return null;
                return data?.session || null;
            }

            return null;
        };

        const run = async () => {
            let session;
            try {
                session = await establishSession();
            } catch {
                bailToLogin('There was a problem verifying your account. Please sign in manually.');
                return;
            }

            if (!session?.user?.id) {
                // No usable payload (cold deep link or a code that couldn't be
                // exchanged) — let login's own session check take over.
                setNoPayload(true);
                return;
            }

            // Same post-auth steps as login.jsx's token sign-in.
            Sentry.setUser({
                id: session.user.id,
                username: session.user.user_metadata?.firstName + ' ' + session.user.user_metadata?.lastName,
                email: session.user.email,
            });
            await AsyncStorage.setItem('CURRENT_USERNAME', session.user.email);
            await registerDevice(isWeb, isAndroid, session.user.id);
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });

            // Website signup stashes the org intent on user_metadata. Create
            // the org here only if the user doesn't already belong to one —
            // create-beta-organization is also idempotent, so a retry or a
            // double-tapped email link can't produce duplicates.
            const metadata = session.user.user_metadata || {};
            const planIntent = metadata.plan === 'paid' ? 'paid' : 'free';

            let organizationID = null;
            let orgCreated = false;

            if (metadata.organizationName) {
                const existingOrgs = await getUsersOrganizations(session.user.id).catch(() => null);

                if (!existingOrgs?.length) {
                    const { data, error } = await supabase.functions.invoke('create-beta-organization', {
                        body: {
                            organizationName: metadata.organizationName,
                            contactName: metadata.contactName,
                            phone: metadata.phone,
                            preferredHandle: metadata.preferredHandle || '',
                            planIntent,
                        },
                    });

                    if (error || data?.error) {
                        bailToLogin('Your email was verified, but we hit a problem creating your organization. Please sign in and try again.');
                        return;
                    }

                    organizationID = data?.organizationID || data?.organization?.id || data?.id || null;
                    orgCreated = true;
                    queryClient.invalidateQueries({ queryKey: QueryKeys.organization.all() });
                }
            }

            if (orgCreated && planIntent === 'paid' && organizationID) {
                // Paid signup → open Stripe setup in the external browser.
                // Failure here is recoverable: the org carries a setup_pending
                // gate, so billing can be finished in-app later — don't strand
                // the user on an error screen.
                try {
                    const { data, error } = await supabase.functions.invoke('change-organization-plan', {
                        body: {
                            organizationID,
                            target: 'paid',
                            returnURL: BILLING_RETURN_URL,
                        },
                    });

                    if (!error && !data?.error && data?.setupUrl) {
                        await openURL(data.setupUrl);
                        // They keep their session — login's checkAuth advances
                        // them to org selection when they return from checkout.
                        router.replace('/auth/login');
                        return;
                    }
                } catch {
                    // Fall through to org selection — billing finishes in-app.
                }
            }

            router.replace('/auth/select-organization');
        };

        run();
    }, []);

    if (noPayload) {
        return <Redirect href="/auth/login" />;
    }

    return (
        <AuthPage hasHeader={false}>
            <AuthHeader eyebrow="Welcome to Reach" title="One more step" description="Finishing your signup..." />
            <ActivityIndicator size="large" color={colors.primary} />
        </AuthPage>
    );
};

export default SignupScreen;
